import { apiGet, apiSend } from './client';
import type { DepositResponse } from './payments';

export interface TonConnectStatus {
  enabled: boolean;
  asset: string;
  network: string;
  transfer_kind?: string;
  jetton_master?: string | null;
  jetton_symbol?: string | null;
  jetton_decimals?: number | null;
  jetton_transfer_ready?: boolean;
  receive_address_configured: boolean;
  min_deposit_azn_minor_override: number | null;
  min_gram?: string;
  tx_valid_seconds: number;
  auto_credit: boolean;
  requires_admin_verification: boolean;
  rate_source: string;
  rate_notes?: string;
}

export interface TonConnectQuote {
  asset: string;
  network: string;
  transfer_kind?: string;
  jetton_master?: string;
  amount_minor: number;
  currency: string;
  usd_azn_rate: string;
  gram_usd_rate?: string;
  ton_usd_rate?: string;
  usd_equivalent_minor: number;
  expected_azn_minor: number;
  crypto_amount: string;
  amount_units?: string;
  amount_nanotons: string;
  gas_nanotons?: string;
  receive_address: string;
  min_azn_minor: number;
  min_gram?: string;
  quote_is_indicative: boolean;
  rate_source: string;
  rate_components?: Record<string, string>;
  quoted_at: string;
  expires_in_seconds: number;
  auto_credit: boolean;
  jetton_transfer_ready?: boolean;
}

export interface TonConnectPaymentRequest {
  validUntil: number;
  transfer_kind?: string;
  jetton_master?: string;
  destination?: string;
  amount_units?: string;
  gas_nanotons?: string;
  forward_ton_amount?: string;
  payload_comment?: string;
  messages: Array<{
    address: string;
    amount: string;
    payload_kind?: string;
    payload_comment?: string;
  }>;
}

export interface TonConnectDepositMeta {
  rail?: string;
  asset?: string;
  network?: string;
  transfer_kind?: string;
  jetton_master?: string;
  jetton_symbol?: string;
  jetton_decimals?: number;
  memo?: string;
  receive_address?: string;
  amount_units?: string;
  gas_nanotons?: string;
  amount_nanotons?: string;
  payment_request?: TonConnectPaymentRequest;
  wallet_confirmed?: boolean;
  sender_address?: string | null;
  tx_hash?: string | null;
  allows_manual_transfer?: boolean;
  jetton_transfer_ready?: boolean;
  auto_credit?: boolean;
  requires_admin_verification?: boolean;
}

export type TonConnectDeposit = DepositResponse & {
  ton_connect?: TonConnectDepositMeta | null;
};

export function fetchTonConnectStatus(): Promise<TonConnectStatus> {
  return apiGet<TonConnectStatus>('/api/v1/payments/ton-connect/status');
}

export function fetchTonConnectQuote(amount_minor: number): Promise<TonConnectQuote> {
  return apiSend<TonConnectQuote>('POST', '/api/v1/payments/ton-connect/quote', { amount_minor });
}

export function createTonConnectDeposit(input: {
  amount_minor: number;
  idempotency_key: string;
}): Promise<TonConnectDeposit> {
  return apiSend<TonConnectDeposit>('POST', '/api/v1/payments/ton-connect/deposits', input);
}

export function refreshTonPaymentRequest(publicId: string): Promise<TonConnectDeposit> {
  return apiSend<TonConnectDeposit>('POST', `/api/v1/payments/ton-connect/deposits/${publicId}/payment-request`);
}

export function submitTonConnectWallet(publicId: string, body: {
  sender_address?: string;
  tx_hash?: string;
  boc?: string;
}): Promise<TonConnectDeposit> {
  return apiSend<TonConnectDeposit>('POST', `/api/v1/payments/ton-connect/deposits/${publicId}/submit`, body);
}

export function shortTonAddress(address: string | null | undefined): string {
  const value = (address || '').trim();
  if (value.length < 12) return value;
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
}
